import type { FormDocument, SchemaType } from '@verifiedit/form-generator'
import type { DocumentMeta } from '@/services/api/site'
import { documentContentUrl } from '@/services/api/documentContent'
import { documentStatusMessage } from '@/types/documents'
import Log from '@/utilities/Log'
import ReportableException from '@/exceptions/ReportableException'
import resourceFunction from '@/services/api/inductionForm'

export async function loadResource(
  resource: string,
  mustBeShown: boolean = false,
): Promise<FormDocument> {
  const [functionName, siteOrCustomerId, documentId] =
    extractFunctionNameAndArguments(resource)

  if (!Object.prototype.hasOwnProperty.call(resourceFunction, functionName)) {
    throw new ReportableException(
      'Undefined resource function',
      {
        availableResourceFunctions: Object.keys(resourceFunction),
        functionName: functionName,
        resource: resource,
      },
      ['SchemaError'],
    )
  }

  const meta: DocumentMeta = await resourceFunction[functionName].apply(
    resourceFunction,
    [Number(siteOrCustomerId), Number(documentId)],
  )

  const document: FormDocument = {
    mimeType: meta.mime ?? null,
    name: meta.name ?? null,
    status: meta.status,
    uuid: meta.uuid,
  }

  if (document.status !== 'available') {
    return explain(document, mustBeShown, meta.message)
  }

  document.content = documentContentUrl(meta)

  return document
}

export async function resolveDocumentResources(
  schema: SchemaType[],
): Promise<void> {
  await Promise.all(schema.map(resolveDocumentResource))
}

async function resolveDocumentResource(component: SchemaType): Promise<void> {
  const resource = component.resource

  if (typeof resource !== 'string') {
    return
  }

  try {
    component.document = await loadResource(resource, component.show)
  } catch (error) {
    Log.notify(error as Error, 'Unresolved document resource')

    // The document keeps its place with wording rather than vanishing from the form: a schema
    // that names a document says the user was meant to see one.
    component.document = explain(
      { name: null, status: 'missing', uuid: resource },
      component.show,
    )
  }
}

/**
 * Wording for a document that cannot be rendered. The api's own wording is preferred when it
 * sent any, except for a document the user must be shown: only the component knows that, so
 * capture's wording is the only one that can say it.
 */
function explain(
  document: FormDocument,
  mustBeShown: boolean,
  apiMessage: string | null | undefined = null,
): FormDocument {
  document.message = mustBeShown
    ? documentStatusMessage(document.status, true)
    : (apiMessage ?? documentStatusMessage(document.status))

  return document
}

function extractFunctionNameAndArguments(resource: string) {
  return resource.replace('@', '').replace('(', ',').replace(')', '').split(',')
}
